import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

export default api;

// ---------- Public ----------
export const getHome = () => api.get("/home").then((r) => r.data);
export const getBooks = (params) => api.get("/books", { params }).then((r) => r.data);
export const getBook = (slug) => api.get(`/books/${slug}`).then((r) => r.data);
export const getAuthors = (params) => api.get("/authors", { params }).then((r) => r.data);
export const getAuthor = (slug) => api.get(`/authors/${slug}`).then((r) => r.data);
export const getCategories = () => api.get("/categories").then((r) => r.data);
export const getResources = () => api.get("/resources").then((r) => r.data);
export const subscribeNewsletter = (email) => api.post("/newsletter", { email }).then((r) => r.data);
export const sendContact = (data) => api.post("/contact", data).then((r) => r.data);
export const sendChurchRequest = (data) => api.post("/iglesias", data).then((r) => r.data);
export const registerDownload = (id) => api.post(`/resources/${id}/download`).then((r) => r.data);
export const sendManuscript = (formData) =>
  api.post("/manuscripts", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);

// ---------- Auth ----------
export const login = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const getCaptcha = () => api.get("/auth/captcha").then((r) => r.data);
export const loginKey = (key, captcha_token, captcha_answer) =>
  api.post("/auth/login-key", { key, captcha_token, captcha_answer }).then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);
export const logoutSession = () => api.post("/auth/logout").then((r) => r.data);

// ---------- Admin ----------
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminBooks = () => api.get("/admin/books").then((r) => r.data);
export const adminCreateBook = (d) => api.post("/admin/books", d).then((r) => r.data);
export const adminUpdateBook = (id, d) => api.put(`/admin/books/${id}`, d).then((r) => r.data);
export const adminDeleteBook = (id) => api.delete(`/admin/books/${id}`).then((r) => r.data);
export const adminDuplicateBook = (id) => api.post(`/admin/books/${id}/duplicate`).then((r) => r.data);
export const adminAuthors = () => api.get("/admin/authors").then((r) => r.data);
export const adminCreateAuthor = (d) => api.post("/admin/authors", d).then((r) => r.data);
export const adminUpdateAuthor = (id, d) => api.put(`/admin/authors/${id}`, d).then((r) => r.data);
export const adminDeleteAuthor = (id) => api.delete(`/admin/authors/${id}`).then((r) => r.data);
export const adminCategories = () => api.get("/admin/categories").then((r) => r.data);
export const adminCreateCategory = (d) => api.post("/admin/categories", d).then((r) => r.data);
export const adminUpdateCategory = (id, d) => api.put(`/admin/categories/${id}`, d).then((r) => r.data);
export const adminDeleteCategory = (id) => api.delete(`/admin/categories/${id}`).then((r) => r.data);
export const adminResources = () => api.get("/admin/resources").then((r) => r.data);
export const adminCreateResource = (d) => api.post("/admin/resources", d).then((r) => r.data);
export const adminUpdateResource = (id, d) => api.put(`/admin/resources/${id}`, d).then((r) => r.data);
export const adminDeleteResource = (id) => api.delete(`/admin/resources/${id}`).then((r) => r.data);
export const adminGetSettings = () => api.get("/admin/settings").then((r) => r.data);
export const adminUpdateSettings = (d) => api.put("/admin/settings", d).then((r) => r.data);
export const adminContacts = () => api.get("/admin/contacts").then((r) => r.data);
export const adminUpdateContact = (id, d) => api.put(`/admin/contacts/${id}`, d).then((r) => r.data);
export const adminDeleteContact = (id) => api.delete(`/admin/contacts/${id}`).then((r) => r.data);
export const adminSubscribers = () => api.get("/admin/subscribers").then((r) => r.data);
export const adminDeleteSubscriber = (id) => api.delete(`/admin/subscribers/${id}`).then((r) => r.data);
export const adminUpload = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

const CHUNK = 8 * 1024 * 1024;
const sendChunk = async (uid, file, i, tries = 4) => {
  const fd = new FormData();
  fd.append("index", String(i));
  fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), `part-${i}`);
  try { await api.post(`/admin/doc-upload/${uid}/chunk`, fd, { timeout: 180000 }); }
  catch (e) {
    if (tries <= 1 || (e?.response && e.response.status < 500)) throw e;
    await new Promise((r) => setTimeout(r, 1500));
    return sendChunk(uid, file, i, tries - 1);
  }
};

export const uploadDocumentChunked = async (file, onProgress, privado = false) => {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const { upload_id } = (await api.post("/admin/doc-upload/init", { filename: file.name, size: file.size, total_chunks: total, privado })).data;
  let next = 0, done = 0;
  const worker = async () => {
    while (next < total) {
      const i = next++;
      await sendChunk(upload_id, file, i);
      done += 1;
      onProgress?.(Math.round((done / total) * 100));
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  return (await api.post(`/admin/doc-upload/${upload_id}/complete`)).data;
};

export const formatBytes = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${(b / 1024 ** 2).toFixed(1)} MB`);

// ---------- Shop ----------
export const shopCheckout = (d) => api.post("/shop/checkout", d).then((r) => r.data);
export const shopPrice = (id) => api.get(`/shop/price/${id}`).then((r) => r.data);

export const shopCapture = (id) => api.post(`/shop/orders/${id}/capture`).then((r) => r.data);
export const adminOrders = () => api.get("/admin/orders").then((r) => r.data);
export const adminCustomers = () => api.get("/admin/customers").then((r) => r.data);
export const adminResendOrder = (id) => api.post(`/admin/orders/${id}/resend`).then((r) => r.data);
export const adminOffers = () => api.get("/admin/offers").then((r) => r.data);
export const adminCreateOffer = (d) => api.post("/admin/offers", d).then((r) => r.data);
export const adminUpdateOffer = (id, d) => api.put(`/admin/offers/${id}`, d).then((r) => r.data);
export const adminDeleteOffer = (id) => api.delete(`/admin/offers/${id}`).then((r) => r.data);
export const fileUrl = (p) => (p && p.startsWith("/") ? `${BACKEND_URL}${p}` : p);
export const formatMoney = (v, cur) => new Intl.NumberFormat("es-ES", { style: "currency", currency: cur }).format(v);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Ha ocurrido un error.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
}
